'use client'

import { useEffect, useState } from 'react'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { Menu, X } from 'lucide-react'
import { Logo } from '@/components/brand/Logo'
import { cn } from '@/lib/utils'
import { storage } from '@/utils/storage'
import { getDefaultRouteForRole } from '@/hoc/withAuth'
import { ROUTES } from '@/config'
import { ButtonLink, Container } from './primitives'

const LINKS = [
  { label: 'Funktionen', href: '/funktionen' },
  { label: 'Preise', href: '/preise' },
  { label: 'Über uns', href: '/ueber-uns' },
  { label: 'Blog', href: '/blog' },
]

const isCurrent = (pathname, href) =>
  pathname === href || pathname?.startsWith(`${href}/`)

export function SiteNav() {
  const pathname = usePathname()
  const [open, setOpen] = useState(false)
  const [portalHref, setPortalHref] = useState(null)
  const [scrolled, setScrolled] = useState(false)

  // Border and shadow appear once the page moves under the nav.
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8)
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  useEffect(() => {
    const token = storage.getAccessToken()
    const user = storage.getUser()
    setPortalHref(token && user ? getDefaultRouteForRole(user.role) : null)
  }, [])

  useEffect(() => {
    if (!open) return
    const onKey = event => event.key === 'Escape' && setOpen(false)
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open])

  const account = portalHref
    ? { label: 'Zum Portal', href: portalHref }
    : { label: 'Anmelden', href: ROUTES.LOGIN }

  return (
    <header
      className={cn(
        'sticky top-0 z-50 border-b backdrop-blur transition-[border-color,box-shadow,background-color] duration-300',
        scrolled
          ? 'border-rule bg-surface/90 shadow-[0_8px_24px_-12px_rgb(var(--ink)/0.12)]'
          : 'border-transparent bg-canvas/70'
      )}
    >
      <Container className="flex h-16 items-center justify-between gap-6">
        <Link
          href="/"
          aria-label="Otti One Startseite"
          className="rounded-control py-2 text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-signal focus-visible:ring-offset-4"
        >
          <Logo className="h-7" title="" />
        </Link>

        <nav
          aria-label="Hauptnavigation"
          className="hidden items-center gap-7 md:flex"
        >
          {LINKS.map(link => (
            <Link
              key={link.href}
              href={link.href}
              aria-current={isCurrent(pathname, link.href) ? 'page' : undefined}
              className={cn(
                'relative py-1 text-[15px] font-medium transition-colors after:absolute after:inset-x-0 after:-bottom-0.5 after:h-[2px] after:origin-left after:rounded-full after:bg-signal after:transition-transform after:duration-300 after:content-[""] hover:text-ink',
                isCurrent(pathname, link.href)
                  ? 'text-ink after:scale-x-100'
                  : 'text-ink/75 after:scale-x-0 hover:after:scale-x-100'
              )}
            >
              {link.label}
            </Link>
          ))}
        </nav>

        <div className="hidden items-center gap-2 md:flex">
          <ButtonLink href={account.href} variant="secondary" size="sm">
            {account.label}
          </ButtonLink>
          <ButtonLink href="/demo" size="sm">
            Demo vereinbaren
          </ButtonLink>
        </div>

        <button
          type="button"
          onClick={() => setOpen(value => !value)}
          aria-expanded={open}
          aria-controls="mobile-menu"
          className="-mr-2 inline-flex h-10 w-10 items-center justify-center rounded-control text-ink md:hidden"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          <span className="sr-only">
            {open ? 'Menü schliessen' : 'Menü öffnen'}
          </span>
        </button>
      </Container>

      {open && (
        <div
          id="mobile-menu"
          className="border-t border-rule bg-surface md:hidden"
        >
          <Container className="flex flex-col pb-5">
            <nav aria-label="Hauptnavigation mobil" className="flex flex-col">
              {LINKS.map(link => (
                <Link
                  key={link.href}
                  href={link.href}
                  onClick={() => setOpen(false)}
                  aria-current={
                    isCurrent(pathname, link.href) ? 'page' : undefined
                  }
                  className={cn(
                    'flex items-center justify-between border-b border-rule py-3.5 text-[17px] font-medium',
                    isCurrent(pathname, link.href) ? 'text-primary' : 'text-ink'
                  )}
                >
                  {link.label}
                </Link>
              ))}
            </nav>
            <div className="mt-5 flex flex-col gap-2">
              <ButtonLink href="/demo" onClick={() => setOpen(false)}>
                Demo vereinbaren
              </ButtonLink>
              <ButtonLink
                href={account.href}
                variant="secondary"
                onClick={() => setOpen(false)}
              >
                {account.label}
              </ButtonLink>
            </div>
          </Container>
        </div>
      )}
    </header>
  )
}
